import { MapFacadeMaplibre } from "src/managers/MapFacadeMaplibre";
import { feature, featureCollection } from "@turf/helpers";
import { bbox, bboxPolygon, difference } from "@turf/turf";
import { setupJPBMapLayers } from "./JPBMapLayers";
import type { JPBReferenceProperty } from "./JPBState";
import { PriceMode, AreaUnit } from "./JPBState";
import { JPBManager } from "./JPBManager";
import type { GeoJSONSource, Map as MaplibreMap } from "maplibre-gl";
import { FeatureLayer } from "src/managers/WmsLayerFunc";
import { toPng } from "html-to-image";

const MASK_SOURCE_ID = 'jpb-export-search-area-mask-source';
const MASK_LAYER_ID = 'jpb-export-search-area-mask';

type RenderJPBOverviewImageOptions = {
   mapStyle: any;
   referenceProperties: JPBReferenceProperty[];
   checkedDocumentIds: string[];
   bufferGeometry?: any | null;
   /**
    * Optional 1-based row index per checked transaction (`documentID → index`).
    * When provided, an extra symbol layer is rendered with these numbers at
    * the upper-left corner of each green circle so the printed map can be
    * cross-referenced with the transactions table (Bilag 3).
    */
   reportRowIndices?: Map<string, number>;
};

const ROW_INDEX_SOURCE_ID = "jpb-export-row-index";
const ROW_INDEX_LAYER_ID = "jpb-export-row-index-labels";

// ============================================================================
// MARK: OVERVIEW WITH JORDBUND IMAGE
// ============================================================================

export async function renderJPBOverviewWithJordbundImage(
   options: RenderJPBOverviewImageOptions
): Promise<Uint8Array | undefined> {
   const { mapStyle, referenceProperties, checkedDocumentIds, bufferGeometry, reportRowIndices } = options;

   const checkedProperties = referenceProperties.filter(prop =>
      checkedDocumentIds.includes(prop.documentID)
   );

   if (!checkedProperties.length) return undefined;

   const { exportMap, container, exportMapFacade } = await createExportMap(mapStyle);

   try {
      await fitMapToFeatures(exportMap, checkedProperties);
      const currentZoom = exportMap.getZoom();
      const dynamicMinZoom = Math.min(currentZoom, 11);

      const jordbundLayer = new FeatureLayer({
         version: "1.3.0",
         label: "Jordbundskort_2024",
         host: "geodata.fvm.dk",
         path: "/geoserver/ows",
         layers: "Jordbunds_og_terraenforhold:Jordbundskort_2024",
         minZoom: dynamicMinZoom,
         maxZoom: 24
      });

      exportMapFacade.addFeatureLayer("Jordbundskort_2024", jordbundLayer);
      await new Promise<void>((resolve) => setTimeout(() => resolve(), 200));

      const checked = new Set<string>(checkedDocumentIds);
      setupJPBMapLayers(exportMap, checkedProperties, checked, PriceMode.Standard, AreaUnit.SquareMeter, {
         onFeatureClick: () => { /* no-op in export map */ }
      });

      if (bufferGeometry) {
         addBufferLayer(exportMap, bufferGeometry);
         setupMaskLayer(exportMap, bufferGeometry);
      }

      if (reportRowIndices && reportRowIndices.size > 0) {
         addRowIndexLayer(exportMap, checkedProperties, reportRowIndices);
      }

      await Promise.race([
         new Promise<void>((resolve) => {
            let idleCount = 0;
            const onIdle = () => {
               idleCount++;
               if (idleCount >= 2) {
                  exportMap.off("idle", onIdle);
                  setTimeout(() => resolve(), 1000);
               }
            };
            exportMap.on("idle", onIdle);
            exportMap.triggerRepaint();
         }),
         new Promise<void>((resolve) => {
            setTimeout(() => {
               console.warn("Jordbund layer loading timeout - proceeding anyway");
               resolve();
            }, 10000);
         })
      ]);

      const mapCanvas = exportMap.getCanvas();
      const compositeCanvas = document.createElement('canvas');
      compositeCanvas.width = mapCanvas.width;
      compositeCanvas.height = mapCanvas.height;
      const compositeCtx = compositeCanvas.getContext('2d');

      if (!compositeCtx) {
         return await captureMapImage(exportMap);
      }

      compositeCtx.drawImage(mapCanvas, 0, 0);
      await drawJordbundLegend(compositeCanvas);
      return await captureCanvasImage(compositeCanvas);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

/**
 * Adds a top-most symbol layer that draws the row index (1-based) at the
 * upper-left corner of every checked transaction's green circle. The text
 * anchors at `bottom-right` so the label sits up-and-left of the point with
 * a tiny overlap, matching the requested layout in the report. This layer
 * is only added inside the export map; the live `JPBMapLayers` is untouched.
 */
function addRowIndexLayer(
   map: MaplibreMap,
   checkedProperties: JPBReferenceProperty[],
   reportRowIndices: Map<string, number>
) {
   const features = checkedProperties
      .map((p) => {
         const idx = reportRowIndices.get(p.documentID);
         if (idx === undefined) return null;
         if (!p.coordinates || !Number.isFinite(p.coordinates.lat) || !Number.isFinite(p.coordinates.lng)) {
            return null;
         }
         return {
            type: "Feature" as const,
            geometry: {
               type: "Point" as const,
               coordinates: [p.coordinates.lng, p.coordinates.lat]
            },
            properties: { rowIndex: idx }
         };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);

   if (features.length === 0) return;

   const data = { type: "FeatureCollection" as const, features };

   if (!map.getSource(ROW_INDEX_SOURCE_ID)) {
      map.addSource(ROW_INDEX_SOURCE_ID, { type: "geojson", data } as any);
   } else {
      const source = map.getSource(ROW_INDEX_SOURCE_ID) as GeoJSONSource;
      source.setData(data as any);
   }

   if (!map.getLayer(ROW_INDEX_LAYER_ID)) {
      map.addLayer({
         id: ROW_INDEX_LAYER_ID,
         type: "symbol",
         source: ROW_INDEX_SOURCE_ID,
         layout: {
            "text-field": ["to-string", ["get", "rowIndex"]],
            "text-size": 11,
            "text-font": ["Noto Sans Bold", "Open Sans Bold", "Arial Unicode MS Bold"],
            // Anchor sits on the bottom-right of the label, placing the label
            // up-and-left of the point with the small offset below.
            "text-anchor": "bottom-right",
            "text-offset": [0.35, -0.35],
            "text-allow-overlap": true,
            "text-ignore-placement": true
         },
         paint: {
            "text-color": "#1a1a1a",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.5
         }
      } as any);
   }
}

// ============================================================================
// MARK: HISTOGRAM IMAGE
// ============================================================================

export async function renderJPBHistogramImage(
   histogramElement: HTMLElement
): Promise<Uint8Array | undefined> {
   const svgElement = histogramElement.querySelector("svg");
   if (!svgElement) {
      console.warn("Could not find SVG element in histogram");
      return undefined;
   }

   const svgRect = svgElement.getBoundingClientRect();
   const svgWidth = svgRect.width || 800;
   const svgHeight = svgRect.height || 400;

   const canvas = document.createElement("canvas");
   canvas.width = svgWidth;
   canvas.height = svgHeight;
   const ctx = canvas.getContext("2d");
   if (!ctx) {
      console.warn("Could not get canvas context");
      return undefined;
   }

   ctx.fillStyle = "#ffffff";
   ctx.fillRect(0, 0, canvas.width, canvas.height);

   const svgData = new XMLSerializer().serializeToString(svgElement);
   const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
   const url = URL.createObjectURL(svgBlob);

   const img = new Image();
   await new Promise<void>((resolve, reject) => {
      img.onload = () => {
         ctx.drawImage(img, 0, 0);
         URL.revokeObjectURL(url);
         resolve();
      };
      img.onerror = () => {
         URL.revokeObjectURL(url);
         reject(new Error("Failed to load SVG image"));
      };
      img.src = url;
   });

   return await captureCanvasImage(canvas);
}

// ============================================================================
// MARK: HELPER FUNCTIONS
// ============================================================================

async function createExportMap(mapStyle: any): Promise<{ exportMap: MaplibreMap; container: HTMLDivElement; exportMapFacade: MapFacadeMaplibre }> {
   const container = document.createElement("div");
   container.style.position = "absolute";
   container.style.left = "-99999px";
   container.style.top = "-99999px";
   container.style.width = "800px";
   container.style.height = "600px";
   container.style.pointerEvents = "none";
   document.body.appendChild(container);

   const exportMapFacade = new MapFacadeMaplibre(container, false, {
      mapStyle,
      interactive: false,
      hash: false,
      canvasContextAttributes: {
         preserveDrawingBuffer: true,
         powerPreference: "high-performance"
      }
   });

   const exportMap = exportMapFacade.getMapPop();

   await new Promise<void>((resolve) => {
      if ((exportMap as any).isStyleLoaded?.()) {
         resolve();
      } else {
         exportMap.once("load", () => resolve());
      }
   });

   return { exportMap, container, exportMapFacade };
}

function cleanupExportMap(exportMapFacade: MapFacadeMaplibre | undefined, container: HTMLDivElement) {
   if (exportMapFacade) {
      try {
         exportMapFacade.getMapPop().remove();
      } catch {
         // ignore
      }
   }
   if (container.parentNode) {
      container.parentNode.removeChild(container);
   }
}

function addBufferLayer(map: MaplibreMap, bufferGeometry: any) {
   const SOURCE_ID = "jpb-export-search-area";
   const OUTLINE_ID = "jpb-export-search-area-outline";
   const combinedBufferGeometry = JPBManager.toCombinedSearchGeometry(bufferGeometry) || bufferGeometry;

   if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
         type: "geojson",
         data: combinedBufferGeometry
      } as any);
   }

   if (!map.getLayer(OUTLINE_ID)) {
      map.addLayer({
         id: OUTLINE_ID,
         type: "line",
         source: SOURCE_ID,
         paint: {
            "line-color": "#d6c251",
            "line-width": 2
         }
      } as any);
   }
}

async function fitMapToFeatures(map: MaplibreMap, properties: JPBReferenceProperty[]) {
   const features = properties.map((prop) => {
      if (prop.geoshape) {
         return feature(prop.geoshape);
      }
      return feature({
         type: "Point",
         coordinates: [prop.coordinates.lng, prop.coordinates.lat]
      });
   });

   const bounds = bbox(featureCollection(features as any));
   map.fitBounds(bounds as any, {
      padding: 40,
      maxZoom: 16
   });

   await waitForMapIdle(map);
}

async function waitForMapIdle(map: MaplibreMap, delay: number = 0) {
   await new Promise<void>((resolve) => {
      map.once("idle", () => {
         if (delay > 0) {
            setTimeout(() => resolve(), delay);
         } else {
            resolve();
         }
      });
   });
}

async function captureMapImage(map: MaplibreMap): Promise<Uint8Array | undefined> {
   const canvas = map.getCanvas();
   const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png");
   });
   if (!blob) return undefined;
   const buf = await blob.arrayBuffer();
   return new Uint8Array(buf);
}

async function captureCanvasImage(canvas: HTMLCanvasElement): Promise<Uint8Array | undefined> {
   const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png");
   });
   if (!blob) return undefined;
   const buf = await blob.arrayBuffer();
   return new Uint8Array(buf);
}

/**
 * Composites the JPB symbol legend (matching `JPBLegendList`) into the bottom-left
 * corner of the given canvas. Drawn directly with the 2D Canvas API so it can be
 * baked into a captured map screenshot for the report.
 */
function drawJPBLegendOverlay(canvas: HTMLCanvasElement) {
   const ctx = canvas.getContext("2d");
   if (!ctx) return;

   const ROW_HEIGHT = 22;
   const PAD_X = 14;
   const PAD_Y = 12;
   const SWATCH_SIZE = 18;
   const TEXT_GAP = 10;
   const FONT = "12px Arial, sans-serif";
   const TITLE_FONT = "bold 13px Arial, sans-serif";
   const ENTRIES: Array<{ label: string; circleColor?: string; overlay?: string; outline?: boolean }> = [
      { label: "Søgeområde", outline: true },
      { label: "Indgår i beregning", circleColor: "#3a8e87" },
      { label: "Ikke valgt", circleColor: "#606060" },
      { label: "Filtreret fra", circleColor: "#1f1f1f" },
      { label: "Bygning – indgår", circleColor: "#3a8e87", overlay: "h" },
      { label: "Bygning – ikke valgt", circleColor: "#606060", overlay: "h" },
      { label: "Bygning – låst", circleColor: "#1f1f1f", overlay: "h" },
      { label: "Datafejl", circleColor: "#1f1f1f", overlay: "!" }
   ];

   ctx.save();
   ctx.font = FONT;
   const textMetrics = ENTRIES.map((e) => ctx.measureText(e.label).width);
   const titleText = "Signaturforklaring";
   ctx.font = TITLE_FONT;
   const titleWidth = ctx.measureText(titleText).width;
   const contentWidth = Math.max(titleWidth, ...textMetrics) + SWATCH_SIZE + TEXT_GAP;
   const boxWidth = contentWidth + PAD_X * 2;
   const boxHeight = PAD_Y * 2 + 22 + ENTRIES.length * ROW_HEIGHT;

   const margin = 12;
   const x = margin;
   const y = canvas.height - boxHeight - margin;

   ctx.fillStyle = "rgba(0,0,0,0.18)";
   ctx.beginPath();
   ctx.roundRect(x + 2, y + 2, boxWidth, boxHeight, 10);
   ctx.fill();

   ctx.fillStyle = "rgba(255,255,255,0.92)";
   ctx.beginPath();
   ctx.roundRect(x, y, boxWidth, boxHeight, 10);
   ctx.fill();

   ctx.strokeStyle = "rgba(0,0,0,0.12)";
   ctx.lineWidth = 1;
   ctx.beginPath();
   ctx.roundRect(x, y, boxWidth, boxHeight, 10);
   ctx.stroke();

   ctx.fillStyle = "#222";
   ctx.font = TITLE_FONT;
   ctx.textBaseline = "top";
   ctx.fillText(titleText, x + PAD_X, y + PAD_Y);

   ctx.font = FONT;
   ENTRIES.forEach((entry, i) => {
      const rowY = y + PAD_Y + 22 + i * ROW_HEIGHT;
      const swatchCx = x + PAD_X + SWATCH_SIZE / 2;
      const swatchCy = rowY + SWATCH_SIZE / 2;

      if (entry.outline) {
         ctx.strokeStyle = "#d6c251";
         ctx.lineWidth = 2;
         ctx.beginPath();
         ctx.arc(swatchCx, swatchCy, SWATCH_SIZE / 2 - 1, Math.PI * 0.5, Math.PI, false);
         ctx.stroke();
      } else if (entry.circleColor) {
         ctx.fillStyle = entry.circleColor;
         ctx.beginPath();
         ctx.arc(swatchCx, swatchCy, SWATCH_SIZE / 2, 0, Math.PI * 2);
         ctx.fill();
         ctx.strokeStyle = "#ffffff";
         ctx.lineWidth = 2;
         ctx.stroke();
         if (entry.overlay) {
            ctx.fillStyle = "#ffffff";
            ctx.font = "bold 11px Arial, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(entry.overlay === "h" ? "⌂" : entry.overlay, swatchCx, swatchCy + 0.5);
            ctx.font = FONT;
            ctx.textAlign = "left";
            ctx.textBaseline = "top";
         }
      }

      ctx.fillStyle = "#222";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(entry.label, x + PAD_X + SWATCH_SIZE + TEXT_GAP, swatchCy);
   });
   ctx.restore();
}

// ============================================================================
// MARK: HEATMAP IMAGE — price-colored circles + legend (Bilag 3)
// ============================================================================

type RenderJPBHeatmapImageOptions = {
   mapStyle: any;
   referenceProperties: JPBReferenceProperty[];
   checkedDocumentIds: string[];
   bufferGeometry?: any | null;
   priceRange?: { min: number; max: number };
};

export async function renderJPBHeatmapImage(
   options: RenderJPBHeatmapImageOptions
): Promise<Uint8Array | undefined> {
   const { mapStyle, referenceProperties, checkedDocumentIds, bufferGeometry, priceRange } = options;
   const checkedProperties = referenceProperties.filter((prop) => checkedDocumentIds.includes(prop.documentID));
   if (!checkedProperties.length) return undefined;

   const { exportMap, container, exportMapFacade } = await createExportMap(mapStyle);
   try {
      const { minPrice, maxPrice, minArea, maxArea } = calculateLegendValues(checkedProperties, priceRange);
      setupPriceColoredCirclesLayer(exportMap, checkedProperties, priceRange);

      if (bufferGeometry) {
         addBufferLayer(exportMap, bufferGeometry);
         setupMaskLayer(exportMap, bufferGeometry);
      }

      await fitMapToFeatures(exportMap, checkedProperties);
      await waitForMapIdle(exportMap);

      const mapCanvas = exportMap.getCanvas();
      const compositeCanvas = document.createElement("canvas");
      compositeCanvas.width = mapCanvas.width;
      compositeCanvas.height = mapCanvas.height;
      const compositeCtx = compositeCanvas.getContext("2d");
      if (!compositeCtx) {
         return await captureMapImage(exportMap);
      }
      compositeCtx.drawImage(mapCanvas, 0, 0);
      drawPriceLegend(compositeCanvas, minPrice, maxPrice, minArea, maxArea);
      return await captureCanvasImage(compositeCanvas);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

function calculateLegendValues(
   properties: JPBReferenceProperty[],
   priceRange?: { min: number; max: number }
) {
   let minPrice = priceRange?.min;
   let maxPrice = priceRange?.max;
   if (minPrice === undefined || maxPrice === undefined) {
      const prices = properties
         .map((p) => p.pricePerSquareMeter)
         .filter((p) => p !== null && p !== undefined && !isNaN(p));
      if (prices.length > 0) {
         minPrice = minPrice ?? Math.min(...prices);
         maxPrice = maxPrice ?? Math.max(...prices);
      } else {
         minPrice = 0;
         maxPrice = 100;
      }
   }
   const areas = properties
      .map((p) => p.area)
      .filter((a) => a !== null && a !== undefined && !isNaN(a) && a > 0);
   const minArea = areas.length > 0 ? Math.min(...areas) : undefined;
   const maxArea = areas.length > 0 ? Math.max(...areas) : undefined;
   return { minPrice: minPrice!, maxPrice: maxPrice!, minArea, maxArea };
}

function setupPriceColoredCirclesLayer(
   map: MaplibreMap,
   properties: JPBReferenceProperty[],
   priceRange?: { min: number; max: number }
) {
   const SOURCE_ID = "jpb-export-price-circles";
   const LAYER_ID = "jpb-export-price-circles-layer";

   let minPrice = priceRange?.min;
   let maxPrice = priceRange?.max;
   if (minPrice === undefined || maxPrice === undefined) {
      const prices = properties
         .map((p) => p.pricePerSquareMeter)
         .filter((p) => p !== null && p !== undefined && !isNaN(p));
      if (prices.length > 0) {
         minPrice = minPrice ?? Math.min(...prices);
         maxPrice = maxPrice ?? Math.max(...prices);
      } else {
         minPrice = 0;
         maxPrice = 100;
      }
   }

   const areas = properties
      .map((p) => p.area)
      .filter((a) => a !== null && a !== undefined && !isNaN(a) && a > 0);
   const minArea = areas.length > 0 ? Math.min(...areas) : 0;
   const maxArea = areas.length > 0 ? Math.max(...areas) : 10000;
   const areaRangeSize = maxArea - minArea || 1;

   const geoJson = {
      type: "FeatureCollection" as const,
      features: properties.map((prop) => {
         const price = prop.pricePerSquareMeter || minPrice;
         const area = prop.area || minArea;
         const color = getPriceColor(price, minPrice!, maxPrice!);
         const normalizedArea = Math.max(0, Math.min(1, (area - minArea) / areaRangeSize));
         const radius = 5 + normalizedArea * 25;
         return {
            id: prop.documentID,
            type: "Feature" as const,
            geometry: { type: "Point" as const, coordinates: [prop.coordinates.lng, prop.coordinates.lat] },
            properties: { documentID: prop.documentID, pricePerSquareMeter: price, area, color, radius }
         };
      })
   };

   if (map.getSource(SOURCE_ID)) {
      const src = map.getSource(SOURCE_ID) as GeoJSONSource;
      if (src) src.setData(geoJson as any);
   } else {
      map.addSource(SOURCE_ID, { type: "geojson", promoteId: "documentID", data: geoJson as any });
   }
   if (!map.getLayer(LAYER_ID)) {
      map.addLayer({
         id: LAYER_ID,
         type: "circle",
         source: SOURCE_ID,
         paint: {
            "circle-color": ["get", "color"],
            "circle-radius": ["get", "radius"],
            "circle-stroke-width": 1.5,
            "circle-stroke-color": "#ffffff",
            "circle-opacity": 0.85
         }
      } as any);
   }
}

function getPriceColor(price: number, minPrice: number, maxPrice: number): string {
   const range = maxPrice - minPrice || 1;
   const t = Math.max(0, Math.min(1, (price - minPrice) / range));
   return `rgb(255, ${Math.round(255 * (1 - t))}, 0)`;
}

function drawPriceLegend(
   canvas: HTMLCanvasElement,
   minPrice: number,
   maxPrice: number,
   minArea?: number,
   maxArea?: number
) {
   const ctx = canvas.getContext("2d");
   if (!ctx) return;

   const padding = 10;
   const cornerRadius = 12;
   const legendWidth = 240;
   const colorBarHeight = 20;
   const sectionSpacing = 15;
   const maxCircleRadius = 18;
   const haveArea = minArea !== undefined && maxArea !== undefined;
   const sizeSectionHeight = haveArea ? 90 : 0;
   const totalHeight = 12 + 8 + colorBarHeight + sectionSpacing + (haveArea ? 8 : 0) + sizeSectionHeight + 10;

   const bgX = canvas.width - legendWidth - padding;
   const bgY = padding;

   ctx.fillStyle = "rgba(0,0,0,0.15)";
   ctx.beginPath();
   ctx.roundRect(bgX + 2, bgY + 2, legendWidth, totalHeight, cornerRadius);
   ctx.fill();
   ctx.fillStyle = "rgba(255,255,255,0.85)";
   ctx.beginPath();
   ctx.roundRect(bgX, bgY, legendWidth, totalHeight, cornerRadius);
   ctx.fill();
   ctx.strokeStyle = "rgba(0,0,0,0.1)";
   ctx.lineWidth = 1;
   ctx.beginPath();
   ctx.roundRect(bgX, bgY, legendWidth, totalHeight, cornerRadius);
   ctx.stroke();

   let currentY = bgY + 12 + 8;
   ctx.fillStyle = "#333";
   ctx.font = "12px Arial";
   ctx.textAlign = "left";
   ctx.textBaseline = "alphabetic";
   ctx.fillText("Pris pr. m²", bgX + 12, currentY);
   currentY += 18;

   const gradientX = bgX + 12;
   const gradientY = currentY;
   const gradientWidth = legendWidth - 24;
   const gradient = ctx.createLinearGradient(gradientX, gradientY, gradientX + gradientWidth, gradientY);
   [
      { pos: 0, color: "rgb(255,255,0)" },
      { pos: 0.25, color: "rgb(255,192,0)" },
      { pos: 0.5, color: "rgb(255,128,0)" },
      { pos: 0.75, color: "rgb(255,64,0)" },
      { pos: 1, color: "rgb(255,0,0)" }
   ].forEach((s) => gradient.addColorStop(s.pos, s.color));
   ctx.fillStyle = gradient;
   ctx.fillRect(gradientX, gradientY, gradientWidth, colorBarHeight);
   ctx.strokeStyle = "rgba(0,0,0,0.3)";
   ctx.lineWidth = 1;
   ctx.strokeRect(gradientX, gradientY, gradientWidth, colorBarHeight);

   ctx.fillStyle = "#666";
   ctx.font = "11px Arial";
   ctx.textAlign = "left";
   ctx.fillText(`${Math.round(minPrice).toLocaleString("da-DK")} kr`, gradientX, gradientY + colorBarHeight + 12);
   ctx.textAlign = "right";
   ctx.fillText(`${Math.round(maxPrice).toLocaleString("da-DK")} kr`, gradientX + gradientWidth, gradientY + colorBarHeight + 12);

   currentY += colorBarHeight + 25;

   if (haveArea) {
      currentY += 8;
      ctx.fillStyle = "#333";
      ctx.font = "12px Arial";
      ctx.textAlign = "left";
      ctx.fillText("Areal (m²)", bgX + 12, currentY);
      currentY += 18;

      const circleY = currentY + 4;
      const circleSpacing = 55;
      const circles = [
         { radius: 5, area: minArea! },
         { radius: 11, area: ((minArea! + maxArea!) / 2) },
         { radius: maxCircleRadius, area: maxArea! }
      ];
      const totalCirclesWidth = (circles.length - 1) * circleSpacing + circles[0].radius * 2;
      const startX = bgX + (legendWidth - totalCirclesWidth) / 2 + circles[0].radius;
      circles.forEach((c, i) => {
         const cx = startX + i * circleSpacing;
         ctx.fillStyle = "rgba(58,142,135,0.3)";
         ctx.beginPath();
         ctx.arc(cx, circleY, c.radius, 0, Math.PI * 2);
         ctx.fill();
         ctx.strokeStyle = "#3a8e87";
         ctx.lineWidth = 1.5;
         ctx.stroke();
         ctx.fillStyle = "#666";
         ctx.font = "10px Arial";
         ctx.textAlign = "center";
         ctx.fillText(`${Math.round(c.area).toLocaleString("da-DK")} m²`, cx, circleY + maxCircleRadius + 12);
      });
   }
}

// ============================================================================
// HELPER FUNCTIONS - Map layers
// ============================================================================

function setupMaskLayer(map: MaplibreMap, bufferGeometry: any) {
   if (!bufferGeometry) {
      if (map.getSource(MASK_SOURCE_ID)) {
         const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
         if (source) {
            source.setData({
               type: 'FeatureCollection',
               features: []
            } as any);
         }
      }
      return;
   }

   try {
      if (!map.getSource(MASK_SOURCE_ID)) {
         map.addSource(MASK_SOURCE_ID, {
            type: 'geojson',
            data: {
               type: 'FeatureCollection',
               features: []
            }
         });
      }

      if (!map.getLayer(MASK_LAYER_ID)) {
         const maskLayerSpec: any = {
            id: MASK_LAYER_ID,
            type: 'fill',
            source: MASK_SOURCE_ID,
            paint: {
               'fill-color': '#000000',
               'fill-opacity': 0.15
            }
         };
         const outlineLayerId = "jpb-export-search-area-outline";
         if (map.getLayer(outlineLayerId)) {
            map.addLayer(maskLayerSpec, outlineLayerId);
         } else {
            map.addLayer(maskLayerSpec);
         }
      }

      const worldBbox: [number, number, number, number] = [-180, -90, 180, 90];
      const worldPolygon = bboxPolygon(worldBbox);
      const bufferFeature = JPBManager.toCombinedSearchGeometry(bufferGeometry);
      if (!bufferFeature) return;

      const maskGeometry = difference(featureCollection([worldPolygon, bufferFeature]));

      if (maskGeometry) {
         const maskFeatureCollection = {
            type: 'FeatureCollection',
            features: [maskGeometry]
         };
         const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
         if (source) {
            source.setData(maskFeatureCollection as any);
         }
      }
   } catch (error) {
      console.warn("Error creating mask layer:", error);
   }
}

// ============================================================================
// HELPER FUNCTIONS - Canvas drawing
// ============================================================================

async function drawJordbundLegend(canvas: HTMLCanvasElement): Promise<void> {
   const legendUrl = "https://geodata.fvm.dk:443/geoserver/ows?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.3.0&FORMAT=image/png&SLD_VERSION=1.1.0&LAYER=Jordbunds_og_terraenforhold:Jordbundskort_2024&STYLE=";

   try {
      const img = new Image();
      img.crossOrigin = "anonymous";

      await new Promise<void>((resolve, reject) => {
         img.onload = () => resolve();
         img.onerror = () => reject(new Error("Failed to load jordbund legend image"));
         img.src = legendUrl;
      });

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const padding = 10;
      const legendWidth = Math.min(img.width, 200);
      const legendHeight = Math.min(img.height, 400);
      const scale = Math.min(legendWidth / img.width, legendHeight / img.height);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;

      const legendX = canvas.width - scaledWidth - padding;
      const legendY = padding;

      const bgPadding = 8;
      const bgX = legendX - bgPadding;
      const bgY = legendY - bgPadding;
      const bgWidth = scaledWidth + (bgPadding * 2);
      const bgHeight = scaledHeight + (bgPadding * 2);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.90)';
      ctx.beginPath();
      ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 8);
      ctx.fill();

      ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 8);
      ctx.stroke();

      ctx.drawImage(img, legendX, legendY, scaledWidth, scaledHeight);
   } catch (error) {
      console.warn("Failed to draw jordbund legend:", error);
   }
}


// ============================================================================
// MARK: V2 REPORT — Subject polygon close-up map
// ============================================================================

/**
 * Renders a close-up of the analyzed subject polygon, highlighted in the same
 * mustard yellow that the live JPB polygon analyzer uses for property outlines
 * (`JPB_ANALYZER_PROPERTY_OUTLINE_COLOR = '#d6c251'`), without comparison points.
 */
export async function renderJPBSubjectPolygonImage(options: {
   mapStyle: any;
   polygonGeoJson: any;
}): Promise<Uint8Array | undefined> {
   const { mapStyle, polygonGeoJson } = options;
   if (!polygonGeoJson) return undefined;

   const { exportMap, container, exportMapFacade } = await createExportMap(mapStyle);
   try {
      const SUBJECT_SOURCE_ID = "jpb-export-subject-polygon";
      const SUBJECT_FILL_ID = "jpb-export-subject-polygon-fill";
      const SUBJECT_OUTLINE_ID = "jpb-export-subject-polygon-outline";

      // Mustard yellow shared with the live polygon analyzer outline so the
      // exported subject polygon matches the on-screen JPB visual language.
      const JPB_POLYGON_COLOR = "#d6c251";

      exportMap.addSource(SUBJECT_SOURCE_ID, {
         type: "geojson",
         data: feature(polygonGeoJson) as any
      } as any);
      exportMap.addLayer({
         id: SUBJECT_FILL_ID,
         type: "fill",
         source: SUBJECT_SOURCE_ID,
         paint: { "fill-color": JPB_POLYGON_COLOR, "fill-opacity": 0.28 }
      } as any);
      exportMap.addLayer({
         id: SUBJECT_OUTLINE_ID,
         type: "line",
         source: SUBJECT_SOURCE_ID,
         paint: { "line-color": JPB_POLYGON_COLOR, "line-width": 3 }
      } as any);

      const polygonBbox = bbox(feature(polygonGeoJson) as any);
      exportMap.fitBounds(polygonBbox as any, { padding: 60, maxZoom: 17 });
      await waitForMapIdle(exportMap);
      return await captureMapImage(exportMap);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

// ============================================================================
// MARK: V2 REPORT — All-points comparison map (no checked-only filter, no heatmap)
// ============================================================================

/**
 * Renders the comparison map showing ALL reference properties (excluded + included),
 * styled the same way they appear on the live map (green / black / grey pins, with houses).
 */
export async function renderJPBAllPointsMapImage(options: {
   mapStyle: any;
   referenceProperties: JPBReferenceProperty[];
   checkedDocumentIds: string[];
   bufferGeometry?: any | null;
}): Promise<Uint8Array | undefined> {
   const { mapStyle, referenceProperties, checkedDocumentIds, bufferGeometry } = options;
   if (!referenceProperties.length) return undefined;

   const { exportMap, container, exportMapFacade } = await createExportMap(mapStyle);
   try {
      const checked = new Set<string>(checkedDocumentIds);
      // Pass ALL reference properties (not just checked) — the map layers will style
      // included vs. excluded points correctly based on the `checked` set.
      setupJPBMapLayers(exportMap, referenceProperties, checked, PriceMode.Standard, AreaUnit.SquareMeter, {
         onFeatureClick: () => { /* no-op in export map */ }
      });

      if (bufferGeometry) {
         addBufferLayer(exportMap, bufferGeometry);
         setupMaskLayer(exportMap, bufferGeometry);
      }

      await fitMapToFeatures(exportMap, referenceProperties);
      await waitForMapIdle(exportMap);

      const mapCanvas = exportMap.getCanvas();
      const compositeCanvas = document.createElement("canvas");
      compositeCanvas.width = mapCanvas.width;
      compositeCanvas.height = mapCanvas.height;
      const ctx = compositeCanvas.getContext("2d");
      if (!ctx) {
         return await captureMapImage(exportMap);
      }
      ctx.drawImage(mapCanvas, 0, 0);
      drawJPBLegendOverlay(compositeCanvas);
      return await captureCanvasImage(compositeCanvas);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

// ============================================================================
// MARK: V2 REPORT — DOM element snapshot (statistics card, polygon analyzer)
// ============================================================================

/**
 * Renders an arbitrary live DOM element to a PNG image. Used to snapshot the JPB statistics
 * card and the polygon analyzer card for the v2 report.
 */
export async function renderDomElementImage(
   element: HTMLElement,
   options: { backgroundColor?: string; pixelRatio?: number } = {}
): Promise<Uint8Array | undefined> {
   if (!element) return undefined;
   try {
      const dataUrl = await toPng(element, {
         backgroundColor: options.backgroundColor ?? "#ffffff",
         pixelRatio: options.pixelRatio ?? 2,
         cacheBust: true,
         // Skip elements that are floating tooltips / portals to avoid cross-origin or off-screen issues.
         filter: (node) => {
            if (!(node instanceof HTMLElement)) return true;
            const cls = node.className;
            if (typeof cls === "string" && cls.includes("ViewButtonToolTip")) return false;
            return true;
         }
      });
      const base64 = dataUrl.split(",")[1];
      if (!base64) return undefined;
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return bytes;
   } catch (e) {
      console.warn("renderDomElementImage failed", e);
      return undefined;
   }
}


declare global {
   interface CanvasRenderingContext2D {
      roundRect(x: number, y: number, width: number, height: number, radius: number): void;
   }
}

if (!CanvasRenderingContext2D.prototype.roundRect) {
   CanvasRenderingContext2D.prototype.roundRect = function (x: number, y: number, width: number, height: number, radius: number) {
      this.beginPath();
      this.moveTo(x + radius, y);
      this.lineTo(x + width - radius, y);
      this.quadraticCurveTo(x + width, y, x + width, y + radius);
      this.lineTo(x + width, y + height - radius);
      this.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      this.lineTo(x + radius, y + height);
      this.quadraticCurveTo(x, y + height, x, y + height - radius);
      this.lineTo(x, y + radius);
      this.quadraticCurveTo(x, y, x + radius, y);
      this.closePath();
   };
}
